import { useEffect, useRef } from "react";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { useMotion } from "./MotionProvider";
import { ENTRANCES, PARALLAX, HOVER_LIFT, MAGNETIC, BUTTON_GLOW } from "./presets";
import { STAGGER, FLOAT } from "./tokens";

// ---------------------------------------------------------------------------
// MOTION HOOKS — the only way components consume animation.
// Every hook: returns a ref; no-ops entirely when motion is disabled
// (prerender / reduced-motion), leaving content in its final visible state.
// Touch devices skip pointer-only effects (parallax / magnetic / lift-follow).
// ---------------------------------------------------------------------------

const isTouch = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none), (pointer: coarse)").matches;

/** Scroll-triggered entrance. preset: "fadeUp" | "fadeBlur" | "scaleIn" | "slideReveal".
 *  opts: { delay, stagger: "tight"|"base"|"loose"|number, children: selector } */
export function useRevealAnimation(preset = "fadeUp", opts = {}) {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current) return;
        const recipe = ENTRANCES[preset] || ENTRANCES.fadeUp;
        const targets = opts.children
            ? ref.current.querySelectorAll(opts.children)
            : ref.current;
        const stagger =
            typeof opts.stagger === "number"
                ? opts.stagger
                : STAGGER[opts.stagger] || 0;

        const ctx = gsap.context(() => {
            gsap.fromTo(targets, recipe.from, {
                ...recipe.to,
                delay: opts.delay || 0,
                stagger,
                scrollTrigger: {
                    trigger: ref.current,
                    start: "top 85%",
                    once: true,
                },
            });
        }, ref);
        return () => ctx.revert();
    }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    return ref;
}

/** Scroll-linked depth. Element drifts at a fraction of scroll speed. */
export function useParallaxLayer(factor = PARALLAX.scrollFactor) {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current) return;
        const ctx = gsap.context(() => {
            gsap.to(ref.current, {
                yPercent: factor * 100,
                ease: "none",
                scrollTrigger: {
                    trigger: ref.current,
                    start: "top bottom",
                    end: "bottom top",
                    scrub: true,
                },
            });
        }, ref);
        return () => ctx.revert();
    }, [enabled, factor]);

    return ref;
}

/** Pointer-follow depth layer (desktop only).
 *  Layered scene depth: give each layer a different intensity and it moves at
 *  its own rate — nearer layers travel further. Smoothed via GSAP quickTo
 *  interpolation: motion trails the cursor and settles softly after it stops.
 *  Never snaps, cannot jitter (one persistent tween per axis, retargeted).
 *  opts (or number = move px):
 *    move    max translation px   (site max: 10)
 *    rotate  max rotation deg     (site max: 5) — 0 disables
 *    lag     smoothing duration s (higher = dreamier trail)
 *    invert  move against the cursor (background layers) */
export function useMouseParallax(opts = {}) {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current || isTouch()) return;
        const o = typeof opts === "number" ? { move: opts } : opts;
        const move = Math.min(o.move ?? PARALLAX.mouseMax, 10);
        const rot = Math.min(o.rotate ?? 0, 5);
        const lag = o.lag ?? 0.7;
        const sign = o.invert ? -1 : 1;

        const el = ref.current;
        const xTo = gsap.quickTo(el, "x", { duration: lag, ease: "luxe" });
        const yTo = gsap.quickTo(el, "y", { duration: lag, ease: "luxe" });
        const rTo = rot
            ? gsap.quickTo(el, "rotation", { duration: lag * 1.2, ease: "luxe" })
            : null;

        const onMove = (e) => {
            const nx = (e.clientX / window.innerWidth) * 2 - 1;  // -1..1
            const ny = (e.clientY / window.innerHeight) * 2 - 1;
            xTo(sign * nx * move);
            yTo(sign * ny * move);
            if (rTo) rTo(sign * nx * rot);
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        return () => {
            window.removeEventListener("pointermove", onMove);
            gsap.to(el, { x: 0, y: 0, rotation: 0, duration: 0.4 });
        };
    }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    return ref;
}

/** Premium card depth (desktop only). The full package:
 *  ENTER  — lift (y/scale) + shadow & gold glow fade in (never snaps)
 *  MOVE   — 3D tilt toward the cursor, max 4°, card subtly follows (x/y drift)
 *           with a DYNAMIC shadow that shifts opposite the tilt, selling the
 *           light source
 *  LEAVE  — everything eases home over ~0.6s on the luxe curve
 *  All via persistent quickTo tweens: retargeted, interpolated, jitter-free. */
export function useTiltCard(maxDeg = 4) {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current || isTouch()) return;
        const el = ref.current;
        const deg = Math.min(maxDeg, 4);
        gsap.set(el, { transformPerspective: 900 });

        const q = (prop, dur = 0.5) => gsap.quickTo(el, prop, { duration: dur, ease: "luxe" });
        const rxTo = q("rotationX"), ryTo = q("rotationY");
        const xTo = q("x", 0.6), yTo = q("y", 0.6);

        // Dynamic shadow: offset tracks tilt so light stays consistent.
        const shadow = { x: 0, y: 0, on: 0 };
        const applyShadow = () => {
            el.style.boxShadow =
                `${-shadow.x}px ${10 + shadow.y}px ${28 + shadow.on * 14}px rgba(0,0,0,${0.35 * shadow.on}), ` +
                `0 0 ${26 * shadow.on}px rgba(198,167,107,${0.22 * shadow.on})`;
        };
        const shadowTo = gsap.quickTo(shadow, "on", { duration: 0.4, ease: "luxe", onUpdate: applyShadow });
        const shX = gsap.quickTo(shadow, "x", { duration: 0.5, ease: "luxe", onUpdate: applyShadow });
        const shY = gsap.quickTo(shadow, "y", { duration: 0.5, ease: "luxe", onUpdate: applyShadow });

        const enter = () => {
            gsap.to(el, { yPercent: -1.2, scale: 1.015, duration: 0.35, ease: "luxe", overwrite: "auto" });
            shadowTo(1);
        };
        const onMove = (e) => {
            const r = el.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width - 0.5;
            const py = (e.clientY - r.top) / r.height - 0.5;
            ryTo(px * deg * 2);
            rxTo(-py * deg * 2);
            xTo(px * 6);          // card follows the mouse slightly
            yTo(py * 6);
            shX(px * deg * 2.5);  // shadow leans opposite the tilt
            shY(py * deg * 2.5);
        };
        const leave = () => {
            rxTo(0); ryTo(0); xTo(0); yTo(0); shX(0); shY(0); shadowTo(0);
            gsap.to(el, { yPercent: 0, scale: 1, duration: 0.6, ease: "luxe", overwrite: "auto" });
        };

        el.addEventListener("pointerenter", enter);
        el.addEventListener("pointermove", onMove, { passive: true });
        el.addEventListener("pointerleave", leave);
        return () => {
            el.removeEventListener("pointerenter", enter);
            el.removeEventListener("pointermove", onMove);
            el.removeEventListener("pointerleave", leave);
            gsap.set(el, { clearProps: "all" });
        };
    }, [enabled, maxDeg]);

    return ref;
}
export const useCardDepth = useTiltCard;


/** Ambient organic drift. Every element — and every AXIS of every element —
 *  gets its own random duration, direction, offset, and delay, so no two
 *  elements (or even two properties) can ever synchronize.
 *  translateX + translateY + rotate + scale. Transform-only. */
export function useFloatingAnimation(opts = {}) {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current) return;
        const o = typeof opts === "number" ? { amplitude: [FLOAT.ampMin, opts] } : opts;
        const [ampMin, ampMax] = o.amplitude || [FLOAT.ampMin, FLOAT.ampMax];
        const r = gsap.utils.random;
        const dir = () => (Math.random() < 0.5 ? -1 : 1);

        const ctx = gsap.context(() => {
            const el = ref.current;
            const axes = [
                { vars: { x: dir() * r(ampMin, ampMax) } },
                { vars: { y: dir() * r(ampMin, ampMax) } },
            ];
            if (o.rotate !== false)
                axes.push({ vars: { rotation: dir() * r(FLOAT.rotMin, FLOAT.rotMax) } });
            if (o.scale !== false)
                axes.push({ vars: { scale: r(FLOAT.scaleMin, FLOAT.scaleMax) } });
            axes.forEach(({ vars }) => {
                const tw = gsap.to(el, {
                    ...vars,
                    duration: r(FLOAT.durMin, FLOAT.durMax),
                    delay: r(0, FLOAT.delayMax),
                    repeat: -1, yoyo: true, repeatRefresh: true, ease: "sine.inOut",
                });
                tw.progress(Math.random());
            });
        }, ref);
        return () => ctx.revert();
    }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    return ref;
}

/** Hover elevation for simple tiles (desktop only). */
export function useHoverLift() {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current || isTouch()) return;
        const el = ref.current;
        const enter = () => gsap.to(el, { y: HOVER_LIFT.y, scale: HOVER_LIFT.scale, duration: HOVER_LIFT.duration });
        const leave = () => gsap.to(el, { y: 0, scale: 1, duration: HOVER_LIFT.duration });
        el.addEventListener("pointerenter", enter);
        el.addEventListener("pointerleave", leave);
        return () => {
            el.removeEventListener("pointerenter", enter);
            el.removeEventListener("pointerleave", leave);
        };
    }, [enabled]);

    return ref;
}

/** Magnetic pull toward the cursor for CTAs (desktop only). */
export function useMagneticButton(strength = MAGNETIC.strength) {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current || isTouch()) return;
        const el = ref.current;
        const xTo = gsap.quickTo(el, "x", { duration: MAGNETIC.snapDuration });
        const yTo = gsap.quickTo(el, "y", { duration: MAGNETIC.snapDuration });

        let rect = null, rectAt = 0;
        const onMove = (e) => {
            const now = performance.now();
            if (!rect || now - rectAt > 200) {
                rect = el.getBoundingClientRect();
                rectAt = now;
            }
            const r = rect;
            const cx = r.left + r.width / 2;
            const cy = r.top + r.height / 2;
            const dx = e.clientX - cx;
            const dy = e.clientY - cy;
            const dist = Math.hypot(dx, dy);
            const reach = MAGNETIC.radius + Math.max(r.width, r.height) / 2;
            if (dist < reach) {
                xTo(dx * strength);
                yTo(dy * strength);
            } else {
                xTo(0);
                yTo(0);
            }
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        return () => {
            window.removeEventListener("pointermove", onMove);
            gsap.set(el, { x: 0, y: 0 });
        };
    }, [enabled, strength]);

    return ref;
}

/** Gold glow on hover for primary buttons. */
export function useButtonGlow() {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current) return;
        const el = ref.current;
        const enter = () =>
            gsap.to(el, { boxShadow: BUTTON_GLOW.boxShadow, duration: BUTTON_GLOW.duration });
        const leave = () =>
            gsap.to(el, { boxShadow: "0 0 0px rgba(198,167,107,0)", duration: BUTTON_GLOW.duration });
        el.addEventListener("pointerenter", enter);
        el.addEventListener("pointerleave", leave);
        return () => {
            el.removeEventListener("pointerenter", enter);
            el.removeEventListener("pointerleave", leave);
        };
    }, [enabled]);

    return ref;
}


/** Premium physical button. Composes with useMagneticButton (different props).
 *  Hover (250ms, "luxe"): scale, gold glow + shadow, border brightening,
 *  background tint — everything TWEENED, never a snapped color change.
 *  Press: scale 0.98; release springs back (back.out overshoot).
 *  variant "fill": hover fills gold with dark text (for outline CTAs). */
export function usePremiumButton(variant = "fill") {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current) return;
        const el = ref.current;
        const d = 0.25;

        const hoverIn = () =>
            gsap.to(el, {
                scale: 1.03,
                boxShadow: "0 6px 24px rgba(0,0,0,0.45), 0 0 28px rgba(198,167,107,0.35)",
                borderColor: "#e3d3b0",
                ...(variant === "fill"
                    ? { backgroundColor: "rgba(198,167,107,1)", color: "#030303" }
                    : { backgroundColor: "rgba(198,167,107,0.08)" }),
                duration: d,
                ease: "luxe",
                overwrite: "auto",
            });
        const hoverOut = () =>
            gsap.to(el, {
                scale: 1,
                boxShadow: "0 0 0 rgba(0,0,0,0), 0 0 0 rgba(198,167,107,0)",
                borderColor: "#c6a76b",
                backgroundColor: "rgba(198,167,107,0)",
                ...(variant === "fill" ? { color: "#c6a76b" } : {}),
                duration: d,
                ease: "luxe",
                overwrite: "auto",
            });
        const press = () =>
            gsap.to(el, { scale: 0.98, duration: 0.09, ease: "power2.out", overwrite: "auto" });
        const release = () =>
            gsap.to(el, { scale: 1.03, duration: 0.45, ease: "back.out(2.4)", overwrite: "auto" });

        el.addEventListener("pointerenter", hoverIn);
        el.addEventListener("pointerleave", hoverOut);
        el.addEventListener("pointerdown", press);
        el.addEventListener("pointerup", release);
        el.addEventListener("pointercancel", hoverOut);
        return () => {
            el.removeEventListener("pointerenter", hoverIn);
            el.removeEventListener("pointerleave", hoverOut);
            el.removeEventListener("pointerdown", press);
            el.removeEventListener("pointerup", release);
            el.removeEventListener("pointercancel", hoverOut);
            gsap.set(el, { clearProps: "scale,boxShadow,borderColor,backgroundColor,color" });
        };
    }, [enabled, variant]);

    return ref;
}


/** Typographic reveal (SplitText).
 *  mode "chars": headings — per-character blur + fade + translateY stagger.
 *  mode "words": paragraphs — per-word stagger (NEVER line-by-line; lines
 *  re-wrap responsively and line-splits read as chunky).
 *  Tuned to complete before reading begins: a heading finishes in well under
 *  a second; a long paragraph caps its total stagger so it never dawdles.
 *  opts: { onLoad: true = play immediately (hero), else on scroll; delay }
 *  Under prerender/reduced-motion: never splits — HTML stays pristine. */
export function useTextReveal(mode = "chars", opts = {}) {
    const ref = useRef(null);
    const { enabled } = useMotion();

    useEffect(() => {
        if (!enabled || !ref.current) return;
        const el = ref.current;
        const split = new SplitText(el, {
            type: mode === "chars" ? "chars,words" : "words",
            aria: "auto",
        });
        const targets = mode === "chars" ? split.chars : split.words;
        // Cap total stagger so long text still finishes promptly.
        const per = mode === "chars" ? 0.018 : 0.026;
        const maxTotal = mode === "chars" ? 0.5 : 0.6;
        const stagger = Math.min(per, maxTotal / Math.max(targets.length, 1));

        const vars = {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: mode === "chars" ? 0.5 : 0.55,
            stagger,
            delay: opts.delay || 0,
            ease: "luxe",
            onComplete: () => split.revert(), // restore clean DOM after play
        };
        gsap.set(targets, { opacity: 0, y: mode === "chars" ? 18 : 14, filter: "blur(6px)" });

        let st;
        if (opts.onLoad) {
            gsap.to(targets, vars);
        } else {
            st = gsap.to(targets, {
                ...vars,
                scrollTrigger: { trigger: el, start: "top 88%", once: true },
            });
        }
        return () => {
            if (st && st.scrollTrigger) st.scrollTrigger.kill();
            split.revert();
        };
    }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    return ref;
}
